import { ApiResponse, Culture, cultures, ListResult } from "../VolunteersHighlightContext/constants";

export const VOLUNTEERS_FAQ_LIST_PATH = "/volunteers-faq";

/* -------------- LIST (response) -------------- */

// The question and the answer come in the culture of the request, or in the other culture when it is missing.
export interface VolunteersFAQListItem {
	faqId: string;
	question: string | null;
	answer: string | null;
	isActive: boolean;
	displayOrder: number;
	creationDate?: string | null;
}

export type VolunteersFAQListApiResponse = ApiResponse<ListResult<VolunteersFAQListItem>>;

/* -------------- GET (response) -------------- */

export interface VolunteersFAQLocalizable {
	faqLocalizableId: string;
	culture: string;
	question: string;
	// Rich text (HTML) written with the CMS editor.
	answer: string;
}

export interface VolunteersFAQDetail {
	faqId: string;
	isActive: boolean;
	displayOrder: number;
	volunteersFAQLocalizable: VolunteersFAQLocalizable[];
}

export type VolunteersFAQGetApiResponse = ApiResponse<VolunteersFAQDetail>;

/* -------------- ADD (form = request) -------------- */

export interface VolunteersFAQLocalizableFormData {
	culture: Culture;
	question: string;
	answer: string;
}

export interface VolunteersFAQFormData {
	isActive: boolean;
	displayOrder?: number;
	volunteersFAQLocalizable: VolunteersFAQLocalizableFormData[];
}

export const getDefaultVolunteersFAQFormData = (): VolunteersFAQFormData => ({
	isActive: false,
	displayOrder: undefined,
	volunteersFAQLocalizable: cultures.map(culture => ({
		culture,
		question: "",
		answer: "",
	})),
});

/* -------------- EDIT (form state, complete) -------------- */

export interface VolunteersFAQEditLocalizable extends VolunteersFAQLocalizableFormData {
	faqLocalizableId: string;
}

export interface VolunteersFAQEditFormData {
	faqId: string;
	isActive: boolean;
	displayOrder?: number;
	volunteersFAQLocalizable: VolunteersFAQEditLocalizable[];
}

// Every culture gets a row so the form always shows both sections; a culture missing on the
// server keeps an empty id and is sent as a new localizable only when the user fills it in.
export const toVolunteersFAQEditFormData = (detail: VolunteersFAQDetail): VolunteersFAQEditFormData => ({
	faqId: detail.faqId,
	isActive: detail.isActive,
	displayOrder: detail.displayOrder,
	volunteersFAQLocalizable: cultures.map(culture => {
		const localizable = detail.volunteersFAQLocalizable.find(item => item.culture === culture);
		return {
			faqLocalizableId: localizable?.faqLocalizableId ?? "",
			culture,
			question: localizable?.question ?? "",
			answer: localizable?.answer ?? "",
		};
	}),
});

/* -------------- UPDATE (PATCH request, partial - only changed fields) -------------- */

export interface UpdateVolunteersFAQLocalizable {
	// The detail response calls this id faqLocalizableId; it is left out for a culture that does not exist yet.
	volunteersFAQLocalizableId?: string;
	culture: string;
	question?: string;
	answer?: string;
}

export interface UpdateVolunteersFAQRequest {
	faqId: string;
	isActive?: boolean;
	displayOrder?: number | null;
	volunteersFAQLocalizable?: UpdateVolunteersFAQLocalizable[];
}

/* -------------- Rules (kept in sync with the backend validator) -------------- */

export const VolunteersFAQRules = {
	questionMaxLength: 250,
	// Counted on the editor's HTML, like the other rich text fields of the panel.
	answerMaxLength: 2000,
	textPattern: /^[\p{L}\p{N}\s.,;:!?'"()\-_/&%+#@]*$/u,
} as const;
